import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import MeetingPanel from "plg/meeting/MeetingPanel";
import type { MeetingStep } from "plg/meeting/MeetingPanel";
import { fetchMeetingRequest } from "plg/api/plgTalentApi";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { trackPlg } from "plg/analytics/events";
import { plgEntry } from "plg/analytics/usePlgEntryTracking";

/**
 * PRD-3334 (prototype index_updated.html, "One CTA: Request a meeting"): every PLG entry point opens the
 * same meeting-request side panel. `sent` is true once a request has been sent ("Meeting requested"), as the
 * portal records it, so it holds on every device. The shortlist stays editable after a request.
 * Outside the PLG shell (standard portal pages) useMeeting() returns null, so shared screens keep
 * their portal behaviour (Request a hire) there.
 */
export interface MeetingContextValue {
  /** `entryPoint`: where it was opened from (meeting_panel_opened.entry_point). */
  open: (entryPoint?: string) => void;
  sent: boolean;
}

const MeetingContext = createContext<MeetingContextValue | null>(null);

export function useMeeting(): MeetingContextValue | null {
  return useContext(MeetingContext);
}

/** Holds whether the panel is open and whether a request was sent; renders the panel. */
export function MeetingProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const customerId = useTalentApi().user?.customerId;
  const { count: shortlistCount } = useShortlist();
  const [step, setStep] = useState<MeetingStep | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!customerId) return undefined;
    let live = true;
    fetchMeetingRequest()
      .then((r) => live && setSent(!!r))
      // Unknown: offer the request; a second one answers MEETING_ALREADY_REQUESTED and the panel says so.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [customerId]);

  // Once sent, every Request a meeting button is disabled (product 2026-11-04); this guard makes sure no entry
  // point opens the panel again, so "Already requested" never shows.
  const open = useCallback(
    (entryPoint = "other") => {
      if (sent) return;
      trackPlg("meeting_panel_opened", { shortlist_count: shortlistCount, entry_point: entryPoint });
      setStep("form");
    },
    [sent, shortlistCount]
  );
  const close = useCallback(
    (to?: string) => {
      setStep(null);
      // The panel's only link out is "Browse" (to the talent network) with nothing shortlisted.
      if (to) navigate(to, { state: plgEntry("empty_state") });
    },
    [navigate]
  );
  const markSent = useCallback(() => setSent(true), []);

  const value = useMemo<MeetingContextValue>(() => ({ open, sent }), [open, sent]);
  return (
    <MeetingContext.Provider value={value}>
      {children}
      {step && <MeetingPanel step={step} onStep={setStep} onClose={close} onSent={markSent} />}
    </MeetingContext.Provider>
  );
}
